import * as React from "react";
import { Link } from "react-router-dom";
import { OrderLink } from "@/components/EntityLink";
import { FilterChips } from "@/components/FilterChips";
import { PAYMENT_LIST_VIEWS, type PaymentListView } from "@ewn/shared";
import {
  dismissPaymentReview,
  listPayments,
  markSurplusRefunded,
  recheckPayment,
  type AdminPaymentRow,
} from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { useUrlParam } from "@/lib/useUrlParam";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/feedback";
import { Field, Textarea } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { PageHeader } from "@/components/ui/page-header";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * Crypto invoices, and the ones that need somebody to look.
 *
 * THE REVIEW VIEW IS THE POINT. A payment is flagged when what arrived is
 * not enough -- underpaid past the tolerance -- or arrived where it cannot be
 * delivered against, or when settlement kept throwing. An overpayment is not
 * flagged: it delivers, and its surplus is owed back from the refund queue.
 * Those are the ones with a person's decision attached, and the dashboard
 * links straight here with `?view=review`.
 *
 * CLOSING ONE DOES NOT RESTART THE MACHINE. The worker keeps the row parked and
 * records who answered for it; this screen only has to say so plainly, because
 * an operator who believed "Close" meant "carry on trying" would use it to ask
 * for a retry it will never perform. See `domain/admin-payments.ts`.
 */
const TONE: Record<string, BadgeTone> = {
  waiting: "amber",
  confirming: "blue",
  settled: "green",
  expired: "gray",
  underpaid: "red",
  superseded: "gray",
};

/** How each list view reads; a Record, so a new view cannot miss its tab. */
const VIEW_COPY: Record<PaymentListView, { label: string; empty: string }> = {
  review: { label: "Needs review", empty: "Nothing is waiting for review." },
  refunds: { label: "Refunds due", empty: "No surplus is owed back." },
};
/** `""` is every payment. */
const ALL = { value: "", label: "All", empty: "No payments yet." } as const;
const VIEWS: readonly { value: PaymentListView | ""; label: string; empty: string }[] = [
  ALL,
  ...PAYMENT_LIST_VIEWS.map((value) => ({ value, ...VIEW_COPY[value] })),
];

export function PaymentsScreen() {
  const [asked, setView] = useUrlParam("view");
  const current = VIEWS.find((v) => v.value === asked) ?? ALL;
  const view = current.value;
  const pager = usePagedCursor(view);
  const { data, loading, error, reload } = useAsync(
    () => listPayments({ view: view || undefined, cursor: pager.cursor }),
    [view, pager.cursor],
  );
  const rows = data?.rows ?? [];
  const [asking, setAsking] = React.useState<Asking | null>(null);
  const recheck = useAction();

  return (
    <div className="space-y-4">
      <PageHeader title="Payments" description="Crypto invoices, newest first.">
        <FilterChips options={VIEWS} value={view} onChange={setView} />
      </PageHeader>

      {recheck.error && <Notice tone="error">{recheck.error}</Notice>}

      <PagedList
        columns={7}
        rows={rows}
        loading={loading}
        error={error}
        empty={current.empty}
        pager={pager}
        nextCursor={data?.nextCursor}
        head={
          <>
            <TH>Coin</TH>
            <TH>Status</TH>
            <TH numeric>Asked</TH>
            <TH numeric>Paid</TH>
            <TH>Order</TH>
            <TH>Review</TH>
            <TH>Opened</TH>
          </>
        }
        renderRow={(payment) => (
          <TR key={payment.payment_id}>
            <TD>
              {/* Ticker AND chain: three enabled coins share a ticker, so the
                  ticker alone does not say which one arrived. */}
              <span className="font-medium">{String(payment.ticker)}</span>{" "}
              <span className="text-xs text-muted-foreground">{String(payment.network_label)}</span>
            </TD>
            <TD>
              <Badge color={TONE[String(payment.status)] ?? "gray"}>{String(payment.status)}</Badge>
            </TD>
            <TD numeric className="font-mono text-xs">
              {String(payment.pay_amount)}
            </TD>
            <TD numeric className="font-mono text-xs">
              {payment.actually_paid == null ? "—" : String(payment.actually_paid)}
            </TD>
            <TD>
              {/* The code, joined from the order row: every order has one, so
                  the link cannot land on a 404. */}
              <OrderLink code={payment.order_code} />
            </TD>
            <TD>
              <ReviewCell
                payment={payment}
                busy={recheck.busy}
                onClose={() => setAsking({ kind: "close", payment })}
                onRecheck={async () => {
                  await recheck.run(() => recheckPayment(payment.payment_id));
                  reload();
                }}
              />
              <SurplusCell payment={payment} onRefund={() => setAsking({ kind: "refund", payment })} />
            </TD>
            <TD className="whitespace-nowrap text-muted-foreground">
              {when(String(payment.created_at))}
            </TD>
          </TR>
        )}
      />

      <NoteDialog
        asking={asking}
        onDone={() => {
          setAsking(null);
          reload();
        }}
        onCancel={() => setAsking(null)}
      />
    </div>
  );
}

/**
 * The review in three states, because they are three answers to one question.
 *
 * Waiting shows Recheck and Close. Answered shows WHO answered -- the
 * operator's own email, which is what `reviewed_by` holds -- so a colleague
 * opening the same payment tomorrow can ask them rather than guess. Neither
 * applies to a payment nothing ever flagged, and that one gets a dash.
 */
function ReviewCell({
  payment,
  busy,
  onClose,
  onRecheck,
}: {
  payment: AdminPaymentRow;
  busy: boolean;
  onClose: () => void;
  onRecheck: () => void;
}) {
  if (payment.reviewed_by) {
    return (
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        closed by {payment.reviewed_by}
      </span>
    );
  }
  // The dash is for an empty cell; a surplus line below fills it.
  if (!payment.manual_review_at) return payment.excess_amount ? null : <>—</>;
  return (
    <div className="flex items-center gap-2">
      <Badge color="red">flagged</Badge>
      {/* Back to the machine, which decides; Close is a person's verdict. */}
      <Button size="sm" variant="outline" disabled={busy} onClick={onRecheck}>
        Recheck
      </Button>
      <Button size="sm" variant="outline" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

/**
 * What an overpaid, delivered payment still owes back, and the button that
 * records it sent. Nothing for a payment that was never overpaid.
 */
function SurplusCell({ payment, onRefund }: { payment: AdminPaymentRow; onRefund: () => void }) {
  if (!payment.excess_amount) return null;
  if (payment.excess_refunded_at) {
    return (
      <div className="mt-1 whitespace-nowrap text-xs text-muted-foreground">
        surplus {payment.excess_amount} {payment.ticker} refunded
      </div>
    );
  }
  return (
    <div className="mt-1 flex items-center gap-2">
      <Badge color="amber">
        owes {payment.excess_amount} {payment.ticker}
      </Badge>
      <Button size="sm" variant="outline" onClick={onRefund}>
        Mark refunded
      </Button>
    </div>
  );
}

type Asking = { kind: keyof typeof NOTE_COPY; payment: AdminPaymentRow };

/** What each note dialog asks, and where its answer goes. */
const NOTE_COPY = {
  close: {
    title: "Close this review",
    question: "What did you find?",
    hint: "Kept on the payment, beside the reason it was flagged.",
    placeholder: "Customer sent a second transfer; matched by hand.",
    confirm: "Close review",
    notice: (
      <>
        This takes the payment off the review queue. It does <strong>not</strong> put it back into
        the automatic checks — settle or refund it yourself first, on the{" "}
        <Link to="/orders" className="underline underline-offset-4">
          Orders
        </Link>{" "}
        screen.
      </>
    ),
    submit: (payment: AdminPaymentRow, note: string) => dismissPaymentReview(payment.payment_id, note),
  },
  refund: {
    title: "Record the refund",
    question: "Where did the surplus go?",
    // The transaction, not the wallet: the note is copied into the audit,
    // which keeps it for good, and an address is the customer's.
    hint: "Kept on the payment. Write the transaction, not the address.",
    placeholder: "0.0041 BTC, tx 9f2c…, 2026-09-25.",
    confirm: "Mark refunded",
    notice: (
      <>
        This moves <strong>no money</strong>. Send the surplus from the wallet first; this only
        records that it went, and takes the payment off the refund queue.
      </>
    ),
    submit: (payment: AdminPaymentRow, note: string) => markSurplusRefunded(payment.payment_id, note),
  },
};

/**
 * The note is REQUIRED, and the worker refuses an empty one too.
 *
 * Checked here so the operator is told before the round trip, and checked there
 * because this screen is not the only thing that can call it -- the rule belongs
 * where the write is. This is the queue where "money arrived that we cannot
 * account for" ends up, and a dismissal with no reason records that somebody
 * closed the question and nothing about the answer. The refund record is the
 * same shape: a note saying what was done, kept on the payment.
 */
function NoteDialog({
  asking,
  onDone,
  onCancel,
}: {
  asking: Asking | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [note, setNote] = React.useState("");
  const action = useAction();
  const id = React.useId();
  const reset = action.reset;

  // Cleared as the dialog opens on a new payment, so last time's verdict is
  // never sitting in the box waiting to be filed against a different invoice.
  React.useEffect(() => {
    if (!asking) return;
    setNote("");
    reset();
  }, [asking, reset]);

  if (!asking) return null;
  const copy = NOTE_COPY[asking.kind];

  async function send() {
    if (!asking || !note.trim()) return;
    if (await action.run(() => copy.submit(asking.payment, note))) onDone();
  }

  return (
    <Modal
      open
      title={copy.title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={action.busy}>
            Cancel
          </Button>
          <Button disabled={action.busy || !note.trim()} onClick={() => void send()}>
            {action.busy ? "Saving…" : copy.confirm}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={copy.question} htmlFor={id} hint={copy.hint}>
          <Textarea
            id={id}
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={copy.placeholder}
          />
        </Field>
        <Notice tone="info">{copy.notice}</Notice>
        {action.error && <Notice tone="error">{action.error}</Notice>}
      </div>
    </Modal>
  );
}
